<template>
  <div class="w-full">
    <div class="font-semibold text-xl flex justify-between w-full">
      <div>Payment</div>
      <div class="ml-2 w-full border-b border-gray-300"></div>
    </div>
    <form id="payment-form" autocomplete="false" class="mt-4">
      <div id="link-authentication-element" ref="linkAuthenticationRef" class="mb-4">
        <!--Stripe.js injects the Link Authentication Element-->
      </div>
      <div id="payment-element" ref="paymentElementRef" class="mb-4">
        <!--Stripe.js injects the Payment Element-->
      </div>
      <div id="payment-message" class="text-red-500 mt-2">{{ message }}</div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { loadStripe, Stripe, StripeElements } from "@stripe/stripe-js";
import { Item } from "@models/Item";
import Config from "@/config/Config";
import { useUserProfileStore } from "@/store/UserProfile";

const { profile } = useUserProfileStore();
const emit = defineEmits(["success", "error", "initailized"]);

interface Props {
  connectedAccountId: string | null;
  clientSecret: string | null;
  lineItems: Item[];
}

let stripe: Stripe | null = null;
const props = defineProps<Props>();
const linkAuthenticationRef = ref(null);
const paymentElementRef = ref(null);
const message = ref("");

let elements: StripeElements | null = null;
const emailAddress = ref("");
if (profile.email && !profile.email.endsWith("@privaterelay.appleid.com")) {
  emailAddress.value = profile.email;
}

// Fetches a payment intent and captures the client secret

const initialize = async () => {
    const options = props.connectedAccountId
      ? { stripeAccount: props.connectedAccountId }
      : undefined;
    stripe = await loadStripe(Config.paymentConfig.stripePublishableKey,
      options
    );
    const appearance = {
      theme: "stripe",
      variables: {
        colorText: "#335081",
      },
    };
    elements = stripe.elements({ appearance, clientSecret: props.clientSecret });

    const linkAuthenticationElement = elements.create("linkAuthentication", { defaultValues: { email: emailAddress.value } });
    linkAuthenticationElement.mount(linkAuthenticationRef.value);

    linkAuthenticationElement.on("change", (event) => {
      emailAddress.value = event.value.email;
    });

    const paymentElementOptions = {
      layout: "tabs",
    };

    const paymentElement = elements.create("payment", paymentElementOptions);
    paymentElement.mount(paymentElementRef.value);
    emit("initailized");

};

const confirmPayment = async () => {
  const result = await stripe.confirmPayment({
    elements,
    confirmParams: {
      // Make sure to change this to your payment completion page
      return_url: Config.paymentConfig.returnUrl,
      receipt_email: emailAddress.value,
    },
    redirect: "if_required",
  });

  // This point will only be reached if there is an immediate error when
  // confirming the payment. Otherwise, your customer will be redirected to
  // your `return_url`. For some payment methods like iDEAL, your customer will
  // be redirected to an intermediate site first to authorize the payment, then
  // redirected to the `return_url`.
  if (result.error) {
    if (result.error.type === "card_error" || result.error.type === "validation_error") {
      message.value = result.error.message ?? "An unexpected error occurred.";
      emit("error", result.error.message ?? "An unexpected error occurred.");
    } else {
      emit("error", "An unexpected error occurred.");
      message.value = "An unexpected error occurred.";
    }
  } else {
    switch (result.paymentIntent?.status) {
      case "succeeded":
        message.value = "Payment succeeded!";
        break;
      case "processing":
        message.value = "Your payment is processing.";
        break;
      case "requires_payment_method":
        message.value = "Your payment was not successful, please try again.";
        break;
      default:
        message.value = "Something went wrong.";
        break;
    }
    message.value = "Payment succeeded!";
    emit("success", result.paymentIntent?.status);
  }
};

// Fetches the payment intent status after payment submission
const checkStatus = async () => {
  const clientSecret = new URLSearchParams(window.location.search).get(
    "payment_intent_client_secret"
  );

  if (!clientSecret) {
    return;
  }

  if (!stripe) {
    return;
  }

  const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

  switch (paymentIntent?.status) {
    case "succeeded":
      message.value = "Payment succeeded!";
      break;
    case "processing":
      message.value = "Your payment is processing.";
      break;
    case "requires_payment_method":
      message.value = "Your payment was not successful, please try again.";
      break;
    default:
      message.value = "Something went wrong.";
      break;
  }
};

const collapse = () => {
  if (paymentElementRef.value != null) {
    const payment = elements.getElement("payment");
    payment.collapse();
  }
};

setTimeout(() => {
  initialize().then(() => checkStatus());
}, 100);

defineExpose({ confirmPayment, collapse, emailAddress });
</script>
